import { inject, Injectable } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { FeatureFlags } from '@app/core/feature-flags/feature-flags';
import { LaunchDarklyService } from '@app/core/feature-flags/launchdarkly.service';
import { MembershipGraphQL } from '@app/core/membership-graphql.service';

import { MembershipRenewalInterstitialService } from './membership-renewal-interstitial.service';

@Injectable({
  providedIn: 'root',
})
class MembershipRenewalHomePageRedirectGuardService {
  constructor(
    private launchDarklyService: LaunchDarklyService,
    private membershipGraphQL: MembershipGraphQL,
    private router: Router,
    private interstitialService: MembershipRenewalInterstitialService,
  ) {}

  canActivate(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const isLimitedAccessEligible$ = this.membershipGraphQL
      .fetch()
      .pipe(map(result => result?.data?.membership?.isLimitedAccessEligible || false));

    const expiredToGuestConversionWeb$ = this.launchDarklyService.featureFlag$(
      FeatureFlags.EXPIRED_TO_GUEST_CONVERSION_WEB,
      false,
    );

    return combineLatest([isLimitedAccessEligible$, expiredToGuestConversionWeb$]).pipe(
      map(([isLimitedAccessEligible, expiredToGuestConversionWeb]) => {
        if (!expiredToGuestConversionWeb) {
          return true;
        }

        const hasSeenInterstitial = this.interstitialService.hasSeenInterstitial();

        if (isLimitedAccessEligible && !hasSeenInterstitial) {
          return this.router.parseUrl('/membership/welcome-back');
        }

        return true;
      }),
    );
  }
}

export const MembershipRenewalHomePageRedirectGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => inject(MembershipRenewalHomePageRedirectGuardService).canActivate(route, state);
